import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { api } from "../api";
import { formatUnixInTimeZone } from "../datetime";
import { useDisplayTimeZone } from "../displayTimezone";

type Row = {
  id: string;
  title: string | null;
  duration_secs: number | null;
  status: string;
  created_at: number;
  preview: string | null;
};

type ListResponse = {
  recordings: Row[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

const LIMIT_OPTIONS = [10, 20, 50] as const;
const SEARCH_DEBOUNCE_MS = 400;

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/** `datetime-local` value → unix seconds (local timezone). */
function parseDateTimeLocal(s: string): number | null {
  if (!s.trim()) return null;
  const ms = new Date(s).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 1000);
}

function statusLabel(s: string) {
  switch (s) {
    case "ready":
      return "Ready";
    case "processing_transcribe":
      return "Transcribing…";
    case "live":
      return "In session";
    case "failed":
      return "Failed";
    default:
      return s;
  }
}

export default function Library() {
  const displayTz = useDisplayTimeZone();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(20);
  const [qInput, setQInput] = useState("");
  const debouncedQ = useDebouncedValue(qInput.trim(), SEARCH_DEBOUNCE_MS);
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");

  const fromUnix = useMemo(() => parseDateTimeLocal(fromInput), [fromInput]);
  const toUnix = useMemo(() => parseDateTimeLocal(toInput), [toInput]);

  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteRowError, setDeleteRowError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (debouncedQ) params.set("q", debouncedQ);
      if (fromUnix != null) params.set("from", String(fromUnix));
      if (toUnix != null) params.set("to", String(toUnix));
      const r = await api<ListResponse>(`/api/recordings?${params.toString()}`);
      if (cancelled) return;
      if (r.ok && r.data) {
        setData(r.data);
        if (r.data.page !== page) setPage(r.data.page);
      } else {
        setData({ recordings: [], total: 0, page: 1, limit, totalPages: 1 });
        setError(r.error || "Could not load library");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, limit, debouncedQ, fromUnix, toUnix, listRefreshKey]);

  const confirmDeleteRecording = useCallback(async (id: string) => {
    setDeleteBusy(true);
    setDeleteRowError(null);
    const r = await api<{ ok?: boolean }>(
      `/api/recordings/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
    setDeleteBusy(false);
    if (!r.ok) {
      setDeleteRowError(r.error || "Could not delete");
      return;
    }
    setDeleteConfirmId(null);
    setListRefreshKey((k) => k + 1);
  }, []);

  function clearFilters() {
    setQInput("");
    setFromInput("");
    setToInput("");
    setPage(1);
  }

  const hasFilters = Boolean(qInput.trim() || fromInput || toInput);

  const rows = data?.recordings ?? [];
  const pendingDelete = useMemo(
    () => (deleteConfirmId ? rows.find((r) => r.id === deleteConfirmId) : undefined),
    [rows, deleteConfirmId],
  );

  if (data === null && loading) {
    return (
      <>
        <h1>Library</h1>
        <div className="card">
          <div className="skeleton" style={{ height: 20 }} />
          <div className="skeleton" style={{ height: 20, marginTop: 8 }} />
        </div>
      </>
    );
  }

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;

  return (
    <>
      <h1>Library</h1>

      <div className="card library-toolbar">
        <div className="library-toolbar__row">
          <div className="field library-toolbar__search">
            <label htmlFor="library-q">Search</label>
            <input
              id="library-q"
              type="search"
              placeholder="Title or transcript…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="field library-toolbar__limit">
            <label htmlFor="library-limit">Per page</label>
            <select
              id="library-limit"
              className="library-select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="library-toolbar__row library-toolbar__row--dates">
          <div className="field">
            <label htmlFor="library-from">From</label>
            <input
              id="library-from"
              type="datetime-local"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="library-to">To</label>
            <input
              id="library-to"
              type="datetime-local"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
            />
          </div>
          {hasFilters ? (
            <div className="library-toolbar__actions">
              <button type="button" className="btn btn-ghost" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
        </div>
        {error ? <p className="error" style={{ marginTop: "0.75rem" }}>{error}</p> : null}
      </div>

      <p className="muted library-meta" aria-live="polite">
        {loading ? "Updating…" : `${total} ${total === 1 ? "entry" : "entries"}`}
        {totalPages > 1 ? ` · Page ${currentPage} of ${totalPages}` : null}
      </p>

      {rows.length === 0 && !loading ? (
        <p className="muted">
          {hasFilters || debouncedQ
            ? "No entries match your filters."
            : "No entries yet. Start a log from Record."}
        </p>
      ) : (
        <div className={loading ? "library-list library-list--dim" : "library-list"}>
          {rows.map((rec) => (
            <div key={rec.id} className="card library-item-card">
              <div className="library-item-row">
                <Link className="library-item-link" to={`/entry/${rec.id}`}>
                  <div className="library-item-head">
                    <div>
                      <strong>
                        {rec.title || formatUnixInTimeZone(rec.created_at, displayTz)}
                      </strong>
                      <p
                        className="muted"
                        style={{ margin: "0.35rem 0 0", fontSize: "0.85rem" }}
                      >
                        {rec.preview ? `${rec.preview}…` : statusLabel(rec.status)}
                      </p>
                    </div>
                    <span className="muted library-item-head__meta">
                      {statusLabel(rec.status)}
                      {rec.duration_secs != null ? ` · ${rec.duration_secs}s` : ""}
                    </span>
                  </div>
                </Link>
                <div className="library-item-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={`Delete log: ${rec.title || formatUnixInTimeZone(rec.created_at, displayTz)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteConfirmId(rec.id);
                      setDeleteRowError(null);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={deleteConfirmId !== null}
        title="Delete entry"
        confirmLabel="Delete"
        busyConfirmLabel="Deleting…"
        confirmVariant="danger"
        busy={deleteBusy}
        error={deleteRowError}
        onCancel={() => {
          setDeleteConfirmId(null);
          setDeleteRowError(null);
        }}
        onConfirm={() => {
          if (deleteConfirmId) void confirmDeleteRecording(deleteConfirmId);
        }}
      >
        <p className="muted" style={{ margin: 0 }}>
          {pendingDelete ? (
            <>
              Permanently delete{" "}
              <strong>
                {pendingDelete.title ||
                  formatUnixInTimeZone(pendingDelete.created_at, displayTz)}
              </strong>
              ? The video and transcript will be removed. This cannot be undone.
            </>
          ) : (
            <>Remove this entry permanently? This cannot be undone.</>
          )}
        </p>
      </ConfirmModal>

      {totalPages > 1 ? (
        <nav className="library-pagination card" aria-label="Library pages">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={loading || currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="library-pagination__status muted">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={loading || currentPage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </>
  );
}
